import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ROUTES } from "@/app/routes";
import { applyNewSession } from "@/features/identity/apply-new-session";
import { acceptInvitation, completeInvitationAcceptance } from "@/features/identity/identity.api";
import {
  markRegistrationCompleted,
  trackSignupStarted,
  trackSignupVerified,
} from "@/shared/analytics/registration-analytics";
import { LogoLockup } from "@/shared/components/brand/logo-mark";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

const route = getRouteApi(ROUTES.auth.acceptInvite.id);

/**
 * Public accept-invite route. Handles two flows:
 *
 * 1. Token in URL → auto-call accept; if existing-user → land in workspace;
 *    if no-user → show displayName form.
 * 2. Manual OTP entry → user types email + 6-digit code.
 */
export function AcceptInviteRoute() {
  const { token } = route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [stage, setStage] = useState<"verifying" | "otp-entry" | "displayName-entry" | "done">(
    token ? "verifying" : "otp-entry",
  );
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [acceptanceToken, setAcceptanceToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({ mutationFn: acceptInvitation });
  const complete = useMutation({ mutationFn: completeInvitationAcceptance });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await accept.mutateAsync({ token });
        if (cancelled) return;
        if (result.action === "signed-in") {
          await applyNewSession(qc);
          await navigate({ to: "/", search: {} });
          setStage("done");
        } else {
          trackSignupStarted("invitation");
          trackSignupVerified("invitation");
          setEmail(result.email);
          setAcceptanceToken(result.acceptanceToken);
          setStage("displayName-entry");
        }
      } catch {
        if (cancelled) return;
        await navigate({ to: "/auth/error", search: { reason: "invalid-or-expired" } });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, qc, accept.mutateAsync]);

  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const result = await accept.mutateAsync({ email, otpCode });
      if (result.action === "signed-in") {
        await applyNewSession(qc);
        await navigate({ to: "/", search: {} });
      } else {
        trackSignupStarted("invitation");
        trackSignupVerified("invitation");
        setEmail(result.email);
        setAcceptanceToken(result.acceptanceToken);
        setStage("displayName-entry");
      }
    } catch {
      setError("That code is invalid or expired.");
    }
  }

  async function onSubmitDisplayName(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!acceptanceToken) return;
    try {
      await complete.mutateAsync({ acceptanceToken, displayName });
      markRegistrationCompleted("invitation");
      await applyNewSession(qc);
      await navigate({ to: "/", search: {} });
    } catch {
      setError("Could not complete signup. Try again.");
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-12">
      <div>
        <LogoLockup className="w-fit" />
        <h1 className="font-display mt-4 text-3xl font-semibold tracking-tighter">
          Accept your invitation
        </h1>
      </div>

      {stage === "verifying" ? (
        <p className="text-sm text-text-secondary">Verifying your invitation…</p>
      ) : stage === "otp-entry" ? (
        <form onSubmit={onSubmitOtp} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Your email</Label>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-otp">6-digit code</Label>
            <Input
              id="invite-otp"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              required
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={accept.isPending}>
            {accept.isPending ? "Verifying…" : "Accept invitation"}
          </Button>
        </form>
      ) : stage === "displayName-entry" ? (
        <form onSubmit={onSubmitDisplayName} className="flex flex-col gap-4">
          <p className="rounded-lg border border-border bg-surface-subtle p-3 text-sm text-text-secondary">
            Welcome! You're joining as <span className="font-medium">{email}</span>. What name
            should your teammates see?
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-name">Display name</Label>
            <Input
              id="invite-name"
              required
              autoFocus
              maxLength={100}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Jamie Doe"
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={complete.isPending}>
            {complete.isPending ? "Joining…" : "Join workspace"}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
